import { useState } from "react";
import { styles } from "../styles";
import BackButton from "../components/BackButton";
import JobCosting from "./JobCosting";
import { hasCapability } from "../utils/capabilities";

/**
 * Archive page for finalized bills (jobs with billFinalized=true). They are
 * filtered OFF the live JobsPage so the active board only shows what still
 * needs work, but the history lives here. Clicking a row expands the job's
 * info + JobCosting inline. JobCosting auto-disables its inputs when the
 * job is finalized (via its own isFinalized check), so the view is
 * effectively read-only. A user can still reopen the bill from JobCosting's
 * own controls, which sends the job back to the live board.
 *
 * Admin / manager (editJobs) only — finalized bills carry rates / margins.
 */
export default function ClosedBillsPage({
  jobsRaw = [],
  customers = [],
  role,
  capabilities,
  actions,
  user,
  profile,
  logoUrl = "",
  orgName = "",
  orgAddress = {},
  onBack,
}) {
  // Dedicated viewClosedBills cap gates this page; default-on in
  // Billing department preset. editJobs still gates the Reopen Bill
  // action inside JobCosting (mutates job state — different concern
  // from read-only PDF re-export).
  const canView = hasCapability(role, capabilities, "viewClosedBills");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  const customerName = (id) =>
    customers.find((c) => c.id === id)?.name || "—";
  const locationName = (cId, lId) => {
    const c = customers.find((x) => x.id === cId);
    return c?.locations.find((l) => l.id === lId)?.name || "—";
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const matchesSearch = (job) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const cust = customers.find((c) => c.id === job.customerId);
    const loc = cust?.locations.find((l) => l.id === job.locationId);
    const haystack = [
      job.jobNumber,
      job.title,
      job.description,
      cust?.name,
      loc?.name,
      job.billFinalizedBy,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  };

  const finalized = [...jobsRaw]
    .filter((j) => j.billFinalized)
    .filter(matchesSearch)
    .sort(
      (a, b) =>
        new Date(b.billFinalizedAt || 0) - new Date(a.billFinalizedAt || 0)
    );

  if (!canView) {
    return (
      <>
        <BackButton onClick={onBack} />
        <h2>Closed Bills</h2>
        <p style={{ fontSize: "13px", color: "#666" }}>
          You don't have permission to view finalized bills.
        </p>
      </>
    );
  }

  const selectedJob = selectedId
    ? jobsRaw.find((j) => j.id === selectedId)
    : null;

  return (
    <>
      <BackButton onClick={onBack} />
      <h2 style={{ margin: "0 0 4px" }}>Closed Bills</h2>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 12px" }}>
        Jobs whose bills have been finalized. Read-only by default — click
        Reopen Bill on a job to send it back to the live board.
      </p>

      <input
        style={{ ...styles.input, maxWidth: "100%" }}
        placeholder="Search by job #, title, customer, location, or finalized-by"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {finalized.length === 0 ? (
        <div
          style={{
            padding: "24px",
            textAlign: "center",
            color: "#888",
            border: "1px dashed #d1d5db",
            borderRadius: "8px",
            marginTop: "12px",
          }}
        >
          {search ? "No finalized bills match your search." : "No finalized bills yet."}
        </div>
      ) : (
        <div style={{ marginTop: "12px" }}>
          {finalized.map((job) => {
            const isOpen = job.id === selectedId;
            return (
              <div
                key={job.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  marginBottom: "10px",
                  background: "white",
                }}
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(isOpen ? null : job.id)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 14px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "15px", fontWeight: 600 }}>
                        {job.jobNumber ? `${job.jobNumber} — ` : ""}
                        {job.title}
                      </div>
                      <div style={{ fontSize: "13px", color: "#555", marginTop: "2px" }}>
                        {customerName(job.customerId)} ·{" "}
                        {locationName(job.customerId, job.locationId)}
                      </div>
                      <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
                        Finalized {fmtDate(job.billFinalizedAt)}
                        {(job.billFinalizedByName || job.billFinalizedBy)
                          ? ` by ${job.billFinalizedByName || job.billFinalizedBy}`
                          : ""}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        flexShrink: 0,
                      }}
                    >
                      {isOpen ? "▾" : "▸"}
                    </div>
                  </div>
                </button>

                {isOpen && selectedJob && (
                  <div
                    style={{
                      padding: "0 14px 14px",
                      borderTop: "1px solid #f0f0f0",
                    }}
                  >
                    {job.description && (
                      <p style={{ fontSize: "13px", margin: "10px 0 6px" }}>
                        <strong>Description:</strong> {job.description}
                      </p>
                    )}
                    <JobCosting
                      job={selectedJob}
                      actions={actions}
                      customer={customers.find((c) => c.id === selectedJob.customerId)}
                      location={customers
                        .find((c) => c.id === selectedJob.customerId)
                        ?.locations?.find((l) => l.id === selectedJob.locationId)}
                      user={user}
                      role={role}
                      capabilities={capabilities}
                      profile={profile}
                      logoUrl={logoUrl}
                      orgName={orgName}
                      orgAddress={orgAddress}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
